import * as React from 'react';

import {CTA, styled, Breakpoints} from '@volkswagen-onehub/components-core';
import {ChevronDown, ChevronUp} from '@volkswagen-onehub/icons-core';
import {AccordionSectionModel} from '../../../../generated/core';
import {AuthoringWrapper} from '../../../components/AuthoringWrapper';
import {useTrackingService} from '../../../context';
import {ContainerItem} from '../../../infrastructure/ContainerExporter';
import {MapTo} from '../../../infrastructure/compatibility/MapTo';
import {C} from '../../../registries/compatibilty';
import {getChildItems} from '../../../utils/container/getContainerChildItems';
import {Parsys, ParsysType} from '../../structure/Parsys';
import {ButtonElement} from '../elements/buttonElement/ButtonElement';
import {HeadingElement} from '../elements/HeadingElement';
import {LinkElement} from '../elements/LinkElementComponent';
import {AccordionItem} from '../items/AccordionItem';
import {SectionWrapper} from './SectionWrapper';
import {useContextTrackingData} from '../../../hooks/useContextTrackingData';
import {resetListStyles} from '../../../d6/components/helpers';
import {useUrlHash} from '../../../hooks/useUrlHash';
const PARSYS_PATH = 'accordionSectionParsys';
const DEFAULT_VISIBLE_ITEMS_COUNT = 4;

const isUrlHashMatchingAnchorId = (
    child: ContainerItem,
    hash: string | undefined
) => hash === child.model.anchorId;

const headline = <HeadingElement path="heading" order="H2" />;

const buttonElement = <ButtonElement path="button" />;

const linkElement = <LinkElement path="link" />;

const StyledAccordionContainer = styled.div`
    --outerPadding: ${props => props.theme.size.grid002};
    --innerPadding: 0;

    display: grid;
    grid-auto-flow: row;
    grid-row-gap: ${props => props.theme.size.dynamic0100};
    grid-template-columns:
        var(--outerPadding) var(--innerPadding) 1fr var(--innerPadding)
        var(--outerPadding);

    @media (min-width: ${Breakpoints.b560}px) {
        --outerPadding: ${props => props.theme.size.grid004};
        --innerPadding: 0;
    }
    @media (min-width: ${Breakpoints.b1600}px) {
        --outerPadding: ${props => props.theme.size.grid006};
    }

    & :target {
        scroll-margin-top: 10vh;
    }
`;
StyledAccordionContainer.displayName = 'StyledAccordionContainer';

const StyledHeadlineWrapper = styled.div`
    grid-column: 2/5;
`;
StyledHeadlineWrapper.displayName = 'StyledHeadlineWrapper';

const StyledButtonWrapper = styled.div`
    width: max-content;
    grid-column: 3/4;
    justify-self: center;
`;
StyledButtonWrapper.displayName = 'StyledButtonWrapper';

const StyledItemsWrapper = styled.ul<{length?: number}>`
    grid-column: 3/4;

    // reset ul styles
    ${resetListStyles()}

    &
    > *:nth-child(
            n + ${props => (props.length || DEFAULT_VISIBLE_ITEMS_COUNT) + 1}
        ):not(${StyledButtonWrapper}) {
        height: 0;
        overflow: hidden;
        visibility: hidden;
    }
`;
StyledItemsWrapper.displayName = 'StyledItemsWrapper';

const StyledAdditionalLinkWrapper = styled.div`
    width: max-content;
    grid-column: 3/4;
`;
StyledHeadlineWrapper.displayName = 'StyledHeadlineWrapper';

function InternalAccordionSection(
    props: AccordionSectionModel
): React.ReactElement {
    const trackingService = useTrackingService();
    const trackingData = useContextTrackingData();
    const [showAllItems, setShowAllItems] = React.useState(false);
    const urlHash = useUrlHash();
    const listItemRef = React.useRef<HTMLLIElement | null>(null);
    const showButtonRef = React.useRef<
        HTMLButtonElement | HTMLAnchorElement | null
    >(null);
    const {
        anchorId,
        mboxId,
        cqItems,
        showMoreButtonLabel,
        showLessButtonLabel,
        showAdditionalLink,
        buttonType,
        contentName,
        contentLabels,
        tagTypeBroken,
        contentId: sectionId
    } = props;
    const childItems = getChildItems(cqItems[PARSYS_PATH]);
    const showHeadline = C.isInEditor() || !cqItems.heading.empty;
    const additionalButtonType =
        buttonType === 'button' ? buttonElement : linkElement;

    const itemsToRenderLength =
        C.isInEditor() || showAllItems
            ? childItems.length
            : DEFAULT_VISIBLE_ITEMS_COUNT;
    const hiddenAccordionItemsCount =
        childItems.length - DEFAULT_VISIBLE_ITEMS_COUNT;

    const toggleButtonLabel = `${showAllItems ? showLessButtonLabel : showMoreButtonLabel} (${hiddenAccordionItemsCount})`;

    const CTAicon = showAllItems ? (
        <ChevronUp variant="small" aria-hidden />
    ) : (
        <ChevronDown variant="small" aria-hidden />
    );

    React.useEffect(() => {
        if (!Boolean(urlHash) || showAllItems) {
            return;
        }
        const isAccordionHidden = childItems.find(
            (child, index) =>
                isUrlHashMatchingAnchorId(child, urlHash) &&
                index >= DEFAULT_VISIBLE_ITEMS_COUNT
        );
        if (!isAccordionHidden) {
            return;
        }
        setShowAllItems(true);
        // *should only render once*
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlHash]);

    const handleShowAllClick = React.useCallback(() => {
        setShowAllItems(!showAllItems);
        trackingService.trackButtonClick(
            sectionId,
            window.location.pathname,
            toggleButtonLabel,
            trackingData
        );
    }, [
        sectionId,
        toggleButtonLabel,
        trackingData,
        showAllItems,
        trackingService
    ]);

    React.useEffect(() => {
        const listItem = listItemRef.current;
        if (!listItem || !showAllItems) return;

        const firstNewAccordion = listItem.querySelector('button');

        if (firstNewAccordion) {
            firstNewAccordion.focus();
        }
    }, [listItemRef, showAllItems]);

    React.useEffect(() => {
        const showButton = showButtonRef.current;

        if (!showButton) return;

        if (showAllItems) return;
        //scroll the showMore button into view after clicking the showLess button
        if (showButton === document.activeElement) {
            showButtonRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [showButtonRef, showAllItems]);

    const renderAccordionItems = () =>
        childItems.map((child: ContainerItem, index: number) => {
            const urlHashMatchesAnchorId = isUrlHashMatchingAnchorId(
                child,
                urlHash
            );
            return (
                <li
                    key={child.key}
                    ref={element => {
                        /* assign a ref only to the first initially invisible accordion,
                the one which will get the focus once the "Show More" button is clicked */
                        if (!element) return;
                        if (index === DEFAULT_VISIBLE_ITEMS_COUNT) {
                            listItemRef.current = element;
                        }
                    }}
                    /* used when not all li items are present in the DOM,
              so the browser can calculate the total and the position of each */
                    aria-setsize={childItems.length}
                    aria-posinset={index + 1}
                >
                    <AccordionItem
                        path={`${PARSYS_PATH}/${child.key}`}
                        defaultOpen={
                            child.model.defaultOpen || urlHashMatchesAnchorId
                        }
                        forceOpen={urlHashMatchesAnchorId}
                    />
                </li>
            );
        });

    return (
        <AuthoringWrapper
            anchorId={anchorId}
            title="S117 Accordion Section"
            bgColor={AuthoringWrapper.BG_COLOR_SECTION}
            tagTypeBroken={tagTypeBroken}
        >
            <SectionWrapper
                mboxId={mboxId}
                anchorId={anchorId}
                sectionId={sectionId}
                contentLabels={contentLabels}
                contentName={contentName}
            >
                <StyledAccordionContainer>
                    <StyledHeadlineWrapper>
                        {showHeadline && headline}
                    </StyledHeadlineWrapper>
                    <StyledItemsWrapper
                        length={itemsToRenderLength}
                        /* Safari removes list semantic when list-stile is set to none.
                        To fix this we need to re-add the role="list" to the element */
                        role="list"
                    >
                        {C.isInEditor() ? (
                            <Parsys
                                path={PARSYS_PATH}
                                parsysType={ParsysType.ITEM_PARSYS}
                            />
                        ) : (
                            renderAccordionItems()
                        )}
                    </StyledItemsWrapper>
                    {childItems.length > DEFAULT_VISIBLE_ITEMS_COUNT ? (
                        <StyledButtonWrapper>
                            <CTA
                                ref={showButtonRef}
                                tag="button"
                                emphasis="secondary"
                                icon={CTAicon}
                                onClick={handleShowAllClick}
                                iconPosition="end"
                            >
                                {toggleButtonLabel}
                            </CTA>
                        </StyledButtonWrapper>
                    ) : null}
                    {showAdditionalLink && (
                        <StyledAdditionalLinkWrapper>
                            {additionalButtonType}
                        </StyledAdditionalLinkWrapper>
                    )}
                </StyledAccordionContainer>
            </SectionWrapper>
        </AuthoringWrapper>
    );
}

export const AccordionSection = MapTo(
    'vwa-ngw18/components/editorial/sections/accordionSection',
    InternalAccordionSection
);
